/**
 * Badge — static pill-shaped label (theme T5c §1.1).
 *
 * Variants mirror the semantic palette.  For clause-risk markers
 * (red/yellow/green) use the RiskPill from the F7 contracts module —
 * different token path (risk.*) with domain-specific meaning.
 */
import type { ComponentType, ReactNode } from 'react';

import { cn } from './cn';

export type BadgeVariant =
  | 'default'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'brand'
  | 'accent';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  icon?: ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  className?: string;
}

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  default: 'bg-slate-100 text-slate-700',
  info: 'bg-brand-primary-50 text-brand-primary-800',
  success: 'bg-success-50 text-success-900',
  warning: 'bg-brand-accent-100 text-brand-accent-900',
  error: 'bg-error-50 text-error-900',
  brand: 'bg-brand-primary-800 text-white',
  accent: 'bg-brand-accent-500 text-brand-accent-900',
};

export function Badge({ children, variant = 'default', icon: Icon, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex h-5 items-center gap-1 rounded-full px-2 text-xs font-medium',
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      {Icon ? <Icon aria-hidden size={12} strokeWidth={2} /> : null}
      {children}
    </span>
  );
}
